import {
  Box,
  Button,
  Center,
  Flex,
  Icon,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useBreakpointValue,
} from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { ListPageHeader, listHeaderRow } from "../../common/components/ListPageHeader";
import { useTranslation } from "react-i18next";
import { FiInbox, FiPlusCircle } from "react-icons/fi";
import { useNavigate, useSearchParams } from "react-router-dom";
import { NeedStatus, NeedWaitingListDto, useListNeedsQuery, type NeedDto } from "../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../common/auth/useLoggedInWithOrgContextState";
import { organizationHasSeveralDepartments } from "../../common/departments/departmentVisibility";
import { urls } from "../../urls";
import { NeedStatusTag } from "./NeedStatusTag";
import { ListLoadError } from "../../common/ListLoadError";
import type { EntityTableColumn } from "../../common/entity-table/EntityTable";
import { FilterBar } from "../../common/entity-table/FilterBar";
import { EVERY_STATUS_WITH_OWN_DRAFTS } from "./ownNeedsQuery";
import { useCreateNeed } from "../../common/needs/useCreateNeed";
import {
  HELD_BY_PROCUREMENT,
  NEED_STATUS_FILTER_OPTIONS,
  useNeedStatusLabel,
} from "../../common/needs/useNeedStatusLabel";
import { useNeedPriorityLabel } from "./useNeedPriorityLabel";
import { usePlannedStartLabel } from "../../common/needs/plannedStart";
import { useNeedValueLabel, useNeedValueText } from "./useNeedValueLabel";
import { displayDate } from "../../common/formatting/displayDate";
import {
  needFigureLabel,
  overviewFigureOf,
  parseListedNeedFigure,
  PERIOD_BOUND_NEED_FIGURES,
} from "../overview/needFigures";


interface Props {
  title: string;
  description: string;
  mineOnly: boolean;
  /** Sync the status filter with the URL `?status=` param (used by sidebar links to Inbox / Approved). */
  urlStatusSync: boolean;
}

const WAITING_LISTS = [NeedWaitingListDto.Queue, NeedWaitingListDto.Mine];

export const NeedsListView = ({ title, description, mineOnly, urlStatusSync }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { me, selectedOrg } = useLoggedInWithOrgContextState();
  const showsDepartment = organizationHasSeveralDepartments(selectedOrg);
  const { create: createNeed, isCreating } = useCreateNeed();
  const statusLabel = useNeedStatusLabel();
  const priorityLabel = useNeedPriorityLabel();
  const plannedStartLabel = usePlannedStartLabel();
  const stacked = useBreakpointValue({ base: true, md: false }, { ssr: false }) ?? false;
  const valueLabel = useNeedValueLabel();
  const valueText = useNeedValueText();

  const statusOptions = mineOnly ? [NeedStatus.Draft, ...NEED_STATUS_FILTER_OPTIONS] : NEED_STATUS_FILTER_OPTIONS;
  const [localStatus, setLocalStatus] = useState<string>("");
  const offeredUrlStatuses = (searchParams.get("status") ?? "")
    .split(",")
    .flatMap((value) => statusOptions.filter((option) => option === value));
  const status = urlStatusSync ? offeredUrlStatuses.join(",") : localStatus;
  const chosenStatuses = statusOptions.filter((option) => status.split(",").includes(option));
  const [search, setSearch] = useState("");
  const oldestFirst = searchParams.get("order") === "oldest";
  const waiting = urlStatusSync ? WAITING_LISTS.find((list) => list === searchParams.get("waiting")) : undefined;
  const figureFrom = searchParams.get("from");
  const figureTo = searchParams.get("to");
  const figure =
    urlStatusSync && figureFrom && figureTo ? parseListedNeedFigure(searchParams.get("figure")) : undefined;
  const figureDepartment = figure ? searchParams.get("department") ?? undefined : undefined;
  const responsible = waiting === NeedWaitingListDto.Mine ? searchParams.get("responsible") ?? undefined : undefined;
  const responsibleName = searchParams.get("responsibleName");
  const filtersActive = status !== "" || search.trim() !== "" || waiting !== undefined || figure !== undefined;

  const everyNeedQuery = mineOnly ? { status: EVERY_STATUS_WITH_OWN_DRAFTS } : {};
  const { data: everyNeed } = useListNeedsQuery(everyNeedQuery, { skip: !filtersActive });
  const { data, isLoading, isError, isFetching, refetch } = useListNeedsQuery(
    waiting
      ? { waiting, responsible }
      : figure && figureFrom && figureTo
        ? {
            figure,
            from: figureFrom,
            to: figureTo,
            ...(status ? { status } : {}),
            ...(figureDepartment ? { department: figureDepartment } : {}),
          }
        : status
          ? { status }
          : everyNeedQuery,
  );

  const needs = useMemo(() => {
    let result = data?.needs ?? [];
    if (mineOnly) result = result.filter((n) => n.createdBy.id === me.id);
    const q = search.trim().toLowerCase();
    if (q) result = result.filter((n) => (n.title ?? "").toLowerCase().includes(q));
    if (oldestFirst && !waiting) result = [...result].sort((a, b) => firstSentAt(a).localeCompare(firstSentAt(b)));
    return result;
  }, [data?.needs, mineOnly, me.id, search, oldestFirst, waiting]);

  const holderFallback = String(t("the responsible buyer"));
  const waitingLabel = figure
    ? [
        needFigureLabel(t, overviewFigureOf(figure)),
        ...(PERIOD_BOUND_NEED_FIGURES.includes(overviewFigureOf(figure)) && figureFrom && figureTo
          ? [`${displayDate(figureFrom)} – ${displayDate(figureTo)}`]
          : []),
        ...offeredUrlStatuses.map(statusLabel),
        ...selectedOrg.departments.filter((department) => department.id === figureDepartment).map((d) => d.name),
      ].join(" · ")
    : String(
        waiting === NeedWaitingListDto.Queue
          ? t("Needs in the queue")
          : responsible
            ? t("With {{name}}", { name: responsibleName ?? holderFallback })
            : t("With you"),
      );

  const filteredHeading = figure ? needFigureLabel(t, overviewFigureOf(figure)) : waiting ? waitingLabel : undefined;

  const clearWaiting = () => {
    const params = new URLSearchParams(searchParams);
    if (figure) ["status", "department"].forEach((key) => params.delete(key));
    ["waiting", "order", "figure", "from", "to", "responsible", "responsibleName"].forEach((key) => params.delete(key));
    setSearchParams(params);
  };

  const setStatus = (next: string) => {
    if (urlStatusSync) {
      const params = new URLSearchParams(searchParams);
      if (next) params.set("status", next);
      else params.delete("status");
      setSearchParams(params);
    } else {
      setLocalStatus(next);
    }
  };

  const filterColumns: EntityTableColumn[] =
    waiting || figure
      ? []
      : [
          {
            key: "status",
            label: t("Status"),
            multiSelectConfig: {
              options: statusOptions.map((option) => ({ value: option, label: statusLabel(option) })),
              selectedValues: chosenStatuses.map((chosen) => ({ value: chosen, label: statusLabel(chosen) })),
              onSelect: (values) => setStatus(values.map((value) => value.value).join(",")),
            },
          },
        ];

  const resetFilters = () => {
    setSearch("");
    if (waiting || figure) clearWaiting();
    else setStatus("");
  };

  const countText = everyNeed
    ? t("{{shown}} of {{count}} needs", { shown: needs.length, count: everyNeed.needs.filter((n) => !mineOnly || n.createdBy.id === me.id).length })
    : null;

  return (
    <Box>
      <ListPageHeader
        heading={filteredHeading ?? title}
        headingColor="smText"
        {...listHeaderRow}
        mb={filteredHeading ? "5" : "2"}
        data-testid="list-header"
        actions={
          <Button colorScheme="teal" leftIcon={<Icon as={FiPlusCircle} />} onClick={createNeed} isLoading={isCreating}>
            {t("Report a need")}
          </Button>
        }
      />
      <Box px="2">
        {!filteredHeading && (
          <Text color="smMuted" mb="5">
            {description}
          </Text>
        )}

        <FilterBar
          columns={filterColumns}
          extraChips={waiting || figure ? [{ label: waitingLabel, onRemove: clearWaiting }] : []}
          onResetAll={resetFilters}
          countText={countText}
          search={{
            value: search,
            onChange: setSearch,
            label: String(t("Search the table")),
            tooltip: String(t("Searches Title")),
          }}
        />

        {isLoading ? (
          <Center py="20">
            <Spinner color="smPrimary" />
          </Center>
        ) : isError ? (
          <ListLoadError
            message={t("Could not load needs.")}
            onRetry={() => void refetch()}
            isRetrying={isFetching}
            reset={
              filtersActive
                ? {
                    label: t("Reset table"),
                    onReset: resetFilters,
                  }
                : undefined
            }
          />
        ) : needs.length === 0 ? (
          <Center
            py="16"
            px="6"
            bg="smBackground"
            rounded="xl"
            border="1px solid"
            borderColor="smBorder"
            flexDirection="column"
            gap="3"
            color="smMuted"
          >
            <Icon as={FiInbox} boxSize="28px" />
            <Text fontWeight="medium" color="smText">
              {mineOnly && !filtersActive ? t("You haven't reported any needs yet") : t("No needs match these filters")}
            </Text>
            <Button colorScheme="teal" size="sm" leftIcon={<Icon as={FiPlusCircle} />} onClick={createNeed} isLoading={isCreating}>
              {t("Report a need")}
            </Button>
          </Center>
        ) : (
          stacked ? (
            <Stack spacing="3">
              {needs.map((n) => (
                <Box
                  key={n.id}
                  as="button"
                  type="button"
                  w="full"
                  textAlign="left"
                  bg="smBackground"
                  rounded="xl"
                  border="1px solid"
                  borderColor="smBorder"
                  p="4"
                  _hover={{ bg: "smBackgroundSecondary" }}
                  onClick={() => navigate(n.canEdit ? urls.app.needs.edit.go(n.id) : urls.app.needs.view.go(n.id))}
                >
                  <Text fontWeight="medium" color="smText" overflowWrap="anywhere" noOfLines={3}>
                    {n.title?.trim() || t("Untitled")}
                  </Text>
                  <Flex mt="2" gap="2" align="center" wrap="wrap">
                    <NeedStatusTag status={n.status} />
                    {n.plannedStart && n.status === NeedStatus.Planned && (
                      <Text fontSize="xs" color={n.plannedStartHasPassed ? "smStatusIssueText" : "smMuted"}>
                        {n.plannedStartHasPassed
                          ? t("{{month}} · passed", { month: plannedStartLabel(n.plannedStart) })
                          : plannedStartLabel(n.plannedStart)}
                      </Text>
                    )}
                  </Flex>
                  <Text mt="2" fontSize="sm" color="smMuted">
                    {valueLabel}: {valueText(n.estimatedValue)}
                  </Text>
                </Box>
              ))}
            </Stack>
          ) : (
          <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" overflow="hidden">
            <TableContainer>
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>{t("Title")}</Th>
                    <Th>{t("Status")}</Th>
                    {showsDepartment && <Th>{t("Department")}</Th>}
                    <Th isNumeric>{valueLabel}</Th>
                    <Th>{t("Priority")}</Th>
                    <Th>{t("Reported by")}</Th>
                    {!mineOnly && <Th>{t("Responsible buyer")}</Th>}
                    <Th>{t("Created")}</Th>
                    <Th>{t("Desired delivery")}</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {needs.map((n) => (
                    <Tr
                      key={n.id}
                      cursor="pointer"
                      _hover={{ bg: "smBackgroundSecondary" }}
                      onClick={() =>
                        navigate(n.canEdit ? urls.app.needs.edit.go(n.id) : urls.app.needs.view.go(n.id))
                      }
                    >
                      <Td fontWeight="medium" color="smText" whiteSpace="normal" minW="48" maxW="sm">
                        <Text overflowWrap="anywhere" noOfLines={3} title={n.title ?? undefined}>
                          {n.title?.trim() || t("Untitled")}
                        </Text>
                      </Td>
                      <Td>
                        <NeedStatusTag status={n.status} />
                        {n.plannedStart && n.status === NeedStatus.Planned && (
                          <Text fontSize="xs" color={n.plannedStartHasPassed ? "smStatusIssueText" : "smMuted"} mt="1">
                            {n.plannedStartHasPassed
                          ? t("{{month}} · passed", { month: plannedStartLabel(n.plannedStart) })
                          : plannedStartLabel(n.plannedStart)}
                          </Text>
                        )}
                      </Td>
                      {showsDepartment && <Td color="smMuted">{n.department?.name ?? "–"}</Td>}
                      <Td color="smMuted" isNumeric>
                        {valueText(n.estimatedValue)}
                      </Td>
                      <Td color="smMuted">{n.priority ? priorityLabel(n.priority) : "–"}</Td>
                      <Td color="smMuted">{personName(n.createdBy)}</Td>
                      {!mineOnly && (
                        <Td color="smMuted">
                          {n.responsibleBuyer
                            ? personName(n.responsibleBuyer)
                            : HELD_BY_PROCUREMENT.includes(n.status)
                              ? t("No one responsible")
                              : "–"}
                        </Td>
                      )}
                      <Td color="smMuted">{formatIsoDate(n.createdAt)}</Td>
                      <Td color="smMuted">{n.deliveryDate ? formatLocalDate(n.deliveryDate) : "–"}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>
          )
        )}
      </Box>
    </Box>
  );
};


const formatIsoDate = (instant: string): string => {
  const d = new Date(instant);
  return Number.isNaN(d.getTime())
    ? instant
    : `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
};
const formatLocalDate = (yyyyMmDd: string): string => {
  const parts = yyyyMmDd.split("-");
  return parts.length === 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : yyyyMmDd;
};
const pad = (n: number): string => (n < 10 ? `0${n}` : `${n}`);

const personName = (p: NeedDto["createdBy"]): string => {
  const name = [p.firstName, p.lastName].filter(Boolean).join(" ").trim();
  return name || "–";
};

const firstSentAt = (need: NeedDto): string =>
  (need.history ?? []).find((entry) => entry.toStatus === NeedStatus.Reported)?.at ?? need.createdAt;
